import type { PipelineNode } from "./game";
import {
  BLOCK_ORDER,
  BLOCKS,
  type BlockKind,
  latencyAfterPredictorMs,
  SLOT_COUNT,
  type Slots,
  totalLatencyMs,
} from "./pipeline";

const STORAGE_KEY = "catalyst-game.pipeline";
const DRAG_THRESHOLD_PX = 5;

type DragSource = { from: "palette" } | { from: "slot"; slot: number };

/**
 * The drag-and-drop pipeline editor. Blocks are dragged from the palette into the slots between
 * RADAR and FLAK, between slots to reorder, or out of the chain to remove them. A click (or Enter)
 * on a palette block adds it to the first empty slot; on a placed block it removes it.
 * Pointer events are used throughout so touch screens work too.
 */
export class PipelineBuilder {
  private slots: Slots = loadSlots();
  private chain: HTMLElement;
  private palette: HTMLElement;
  private info: HTMLElement;
  private stats: HTMLElement;
  private slotEls: HTMLElement[] = [];
  private radarEl!: HTMLElement;
  private flakEl!: HTMLElement;

  constructor(root: HTMLElement) {
    root.innerHTML = `
      <div class="chain" role="list" aria-label="Pipeline"></div>
      <div class="builder-foot">
        <div class="palette" role="list" aria-label="Blocks"></div>
        <div class="builder-side">
          <p class="pipe-stats"></p>
          <p class="block-info">Drag blocks into the pipeline. Hover a block to see what it does.</p>
        </div>
      </div>`;
    this.chain = root.querySelector(".chain")!;
    this.palette = root.querySelector(".palette")!;
    this.info = root.querySelector(".block-info")!;
    this.stats = root.querySelector(".pipe-stats")!;
    this.buildChain();
    this.render();
  }

  get(): Slots {
    return this.slots;
  }

  flash(node: PipelineNode, passed: boolean, reason?: string): void {
    const el = node === "radar" ? this.radarEl : node === "flak" ? this.flakEl : this.slotEls[node];
    if (!el) return;
    const cls = passed ? "flash-pass" : "flash-drop";
    el.classList.remove("flash-pass", "flash-drop");
    void el.offsetWidth; // restart the CSS animation
    el.classList.add(cls);
    if (!passed && reason) el.dataset.reason = reason;
    if (typeof node === "number" || node === "radar") {
      const wire = el.nextElementSibling;
      if (passed && wire?.classList.contains("wire")) {
        wire.classList.remove("pulse");
        void (wire as HTMLElement).offsetWidth;
        wire.classList.add("pulse");
      }
    }
  }

  private buildChain(): void {
    this.radarEl = node("radar", "RADAR", "source");
    this.flakEl = node("flak", "FLAK", "sink");
    this.chain.append(this.radarEl, wire());
    for (let i = 0; i < SLOT_COUNT; i++) {
      const slot = document.createElement("div");
      slot.className = "slot";
      slot.dataset.slot = String(i);
      slot.setAttribute("role", "listitem");
      this.slotEls.push(slot);
      this.chain.append(slot, wire());
    }
    this.chain.append(this.flakEl);
  }

  private render(): void {
    this.slotEls.forEach((el, i) => {
      el.replaceChildren();
      const kind = this.slots[i];
      el.classList.toggle("filled", !!kind);
      if (kind) el.append(this.blockEl(kind, { from: "slot", slot: i }));
      else el.append(Object.assign(document.createElement("span"), { className: "slot-hint", textContent: "drop" }));
    });

    this.palette.replaceChildren();
    for (const kind of BLOCK_ORDER) {
      const el = this.blockEl(kind, { from: "palette" });
      if (this.slots.includes(kind)) {
        el.classList.add("used");
        el.setAttribute("aria-disabled", "true");
      }
      this.palette.append(el);
    }

    const total = totalLatencyMs(this.slots);
    const after = latencyAfterPredictorMs(this.slots);
    this.stats.innerHTML =
      `Latency <b>${total} ms</b>` +
      (after === null ? ` · <span class="warn">no Lead Predictor</span>` : ` · after predictor <b>${after} ms</b>`);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.slots));
    } catch {
      // Storage can be unavailable (private mode); the pipeline just won't be remembered.
    }
  }

  private blockEl(kind: BlockKind, source: DragSource): HTMLElement {
    const def = BLOCKS[kind];
    const el = document.createElement("button");
    el.type = "button";
    el.className = `block block-${def.tag}`;
    el.dataset.kind = kind;
    el.innerHTML = `<span class="block-tag">${def.tag}</span><span class="block-label">${def.label}</span><span class="block-lat">+${def.latencyMs} ms</span>`;
    el.title = def.description;
    el.setAttribute("aria-label", `${def.label}. ${def.description} ${source.from === "slot" ? "Press to remove." : "Press to add."}`);

    const describe = () => (this.info.innerHTML = `<b>${def.label}</b> (+${def.latencyMs} ms): ${def.description}`);
    el.addEventListener("pointerenter", describe);
    el.addEventListener("focus", describe);
    el.addEventListener("click", (e) => {
      // A pointer drag ends with a synthetic click; only keyboard clicks (detail 0) get here unhandled.
      if (e.detail !== 0) return;
      this.tap(kind, source);
    });
    el.addEventListener("pointerdown", (e) => this.startDrag(e, el, kind, source));
    return el;
  }

  private tap(kind: BlockKind, source: DragSource): void {
    if (source.from === "slot") {
      this.slots[source.slot] = null;
    } else {
      if (this.slots.includes(kind)) return;
      const empty = this.slots.indexOf(null);
      if (empty < 0) {
        this.info.textContent = "The pipeline is full. Remove a block first.";
        return;
      }
      this.slots[empty] = kind;
    }
    this.render();
  }

  private startDrag(e: PointerEvent, el: HTMLElement, kind: BlockKind, source: DragSource): void {
    if (e.button !== 0) return;
    if (source.from === "palette" && this.slots.includes(kind)) return;
    e.preventDefault();

    const startX = e.clientX;
    const startY = e.clientY;
    const rect = el.getBoundingClientRect();
    let ghost: HTMLElement | null = null;
    let over: HTMLElement | null = null;

    const move = (ev: PointerEvent) => {
      if (!ghost) {
        if (Math.hypot(ev.clientX - startX, ev.clientY - startY) < DRAG_THRESHOLD_PX) return;
        ghost = el.cloneNode(true) as HTMLElement;
        ghost.classList.add("ghost");
        ghost.style.width = `${rect.width}px`;
        document.body.append(ghost);
        el.classList.add("dragging");
        document.body.classList.add("is-dragging");
      }
      ghost.style.transform = `translate(${ev.clientX - rect.width / 2}px, ${ev.clientY - rect.height / 2}px)`;
      const target = this.slotAt(ev.clientX, ev.clientY);
      if (target !== over) {
        over?.classList.remove("over");
        target?.classList.add("over");
        over = target;
      }
    };

    const end = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      over?.classList.remove("over");
      document.body.classList.remove("is-dragging");
      el.classList.remove("dragging");
      if (!ghost) {
        if (ev.type === "pointerup") this.tap(kind, source);
        return;
      }
      ghost.remove();
      if (ev.type === "pointercancel") return;
      const target = this.slotAt(ev.clientX, ev.clientY);
      this.drop(kind, source, target ? Number(target.dataset.slot) : null);
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
  }

  private slotAt(x: number, y: number): HTMLElement | null {
    return this.slotEls.find((s) => {
      const r = s.getBoundingClientRect();
      return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
    }) ?? null;
  }

  private drop(kind: BlockKind, source: DragSource, target: number | null): void {
    if (source.from === "slot") {
      if (target === null) {
        this.slots[source.slot] = null; // dragged out of the chain: remove
      } else {
        const displaced = this.slots[target] ?? null;
        this.slots[target] = kind;
        this.slots[source.slot] = target === source.slot ? kind : displaced;
      }
    } else if (target !== null) {
      this.slots[target] = kind; // a displaced block returns to the palette
    }
    this.render();
  }
}

function node(kind: "radar" | "flak", label: string, tag: string): HTMLElement {
  const el = document.createElement("div");
  el.className = `node node-${kind}`;
  el.setAttribute("role", "listitem");
  el.innerHTML = `<span class="block-tag">${tag}</span><span class="block-label">${label}</span>`;
  return el;
}

function wire(): HTMLElement {
  const el = document.createElement("div");
  el.className = "wire";
  el.setAttribute("aria-hidden", "true");
  return el;
}

function loadSlots(): Slots {
  const empty: Slots = Array.from({ length: SLOT_COUNT }, () => null);
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null") as unknown;
    if (!Array.isArray(raw) || raw.length !== SLOT_COUNT) return empty;
    const seen = new Set<string>();
    return raw.map((k) => {
      if (typeof k !== "string" || !(k in BLOCKS) || seen.has(k)) return null;
      seen.add(k);
      return k as BlockKind;
    });
  } catch {
    return empty;
  }
}
